import type { ErrorBody, ErrorCode } from '@command620/shared';

/** All data comes through this client (CTQ #1: no DB access, no secrets in the browser). */
export const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'network' | 'unknown',
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Handler = () => void;
const defaultUnauthorized: Handler = () => {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  if (window.location.pathname !== '/login') window.location.assign(`/login?next=${next}`);
};
let onUnauthorized: Handler = defaultUnauthorized;
/** AuthProvider registers a router-friendly handler; default is a hard redirect to /login. */
export const setUnauthorizedHandler = (h: Handler | null) => {
  onUnauthorized = h ?? defaultUnauthorized;
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Auth endpoints: a 401 is an answer, not a session loss. */
  skipAuthRedirect?: boolean;
}

const toQuery = (q?: RequestOptions['query']) => {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

async function send(path: string, o: RequestOptions): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}${toQuery(o.query)}`, {
      method: o.method ?? 'GET',
      credentials: 'include',
      signal: o.signal,
      headers: { Accept: 'application/json', ...(o.body !== undefined && { 'Content-Type': 'application/json' }) },
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', "Couldn't reach the server. Check your connection and retry.");
  }
}

let refreshing: Promise<boolean> | null = null;
const refresh = () =>
  (refreshing ??= send('/auth/refresh', { method: 'POST' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    }));

export async function api<T = void>(path: string, o: RequestOptions = {}): Promise<T> {
  let res = await send(path, o);
  // Access token is short-lived: one silent refresh (single-flight), then retry once.
  if (res.status === 401 && !o.skipAuthRedirect && !path.startsWith('/auth/') && (await refresh())) {
    res = await send(path, o);
  }
  if (res.status === 401 && !o.skipAuthRedirect) onUnauthorized();
  if (!res.ok) {
    const b = (await res.json().catch(() => null)) as Partial<ErrorBody> | null;
    throw new ApiError(res.status, b?.error ?? 'unknown', b?.message ?? `Request failed (${res.status}).`, b?.details);
  }
  if (res.status === 204) return undefined as T;
  if (res.status === 202) {
    const text = await res.text(); // bulk endpoints answer 202 with counts; a bare 202 is empty
    return (text ? JSON.parse(text) : undefined) as T;
  }
  return (await res.json()) as T;
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
